import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MessageSquare, Mail, CreditCard, Calendar, Database, Cloud, Smartphone, BarChart3, CheckCircle } from 'lucide-react';

export default function Integrations() {
  const navigate = useNavigate();

  const integrations = [
    {
      name: 'WhatsApp Business',
      description: 'Send automated booking confirmations, reminders, and promotional messages directly to customers.',
      icon: MessageSquare,
      status: 'available',
      features: ['Booking confirmations', 'Appointment reminders', 'Feedback requests', 'Marketing campaigns']
    },
    {
      name: 'Email (Resend)',
      description: 'Professional email communications for invoices, reports, and customer engagement.',
      icon: Mail,
      status: 'available',
      features: ['Transactional emails', 'Invoice delivery', 'Weekly reports', 'Custom templates']
    },
    {
      name: 'Razorpay',
      description: 'Accept payments via UPI, cards, net banking, and wallets with automatic invoice generation.',
      icon: CreditCard,
      status: 'available',
      features: ['Subscription billing', 'One-time payments', 'Refund management', 'Payment analytics']
    },
    {
      name: 'Google Calendar',
      description: 'Sync therapist schedules and bookings with Google Calendar for seamless scheduling.',
      icon: Calendar,
      status: 'coming_soon',
      features: ['Two-way sync', 'Conflict detection', 'Availability updates', 'Team calendars']
    },
    {
      name: 'MongoDB Atlas',
      description: 'Enterprise-grade cloud database with automatic backups and global distribution.',
      icon: Database,
      status: 'available',
      features: ['99.99% uptime', 'Auto-scaling', 'Point-in-time recovery', 'Encryption at rest']
    },
    {
      name: 'Cloud Hosting',
      description: 'Deployed on Railway with global CDN for fast, reliable access worldwide.',
      icon: Cloud,
      status: 'available',
      features: ['Auto-scaling', 'SSL/TLS', 'DDoS protection', 'Daily backups']
    },
    {
      name: 'Mobile App',
      description: 'Native mobile apps for therapists and managers to access on the go.',
      icon: Smartphone,
      status: 'coming_soon',
      features: ['iOS & Android', 'Push notifications', 'Offline mode', 'Biometric login']
    },
    {
      name: 'Advanced Analytics',
      description: 'AI-powered insights and predictive analytics for business optimization.',
      icon: BarChart3,
      status: 'coming_soon',
      features: ['Revenue forecasting', 'Customer insights', 'Trend analysis', 'Custom reports']
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <nav className="fixed top-0 w-full z-40 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/welcome')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Integrations</h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Connect WellNest Technologies with the tools you already use. Seamless integrations to supercharge your spa operations.
          </p>
        </div>

        {/* Integration Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {integrations.map((integration, index) => (
            <Card key={index} className="bg-slate-900 border-slate-700 hover:border-teal-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-600/20 flex items-center justify-center flex-shrink-0">
                    <integration.icon className="w-6 h-6 text-teal-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{integration.name}</h3>
                      {integration.status === 'available' ? (
                        <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
                          <CheckCircle className="w-3 h-3 mr-1" /> Available
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/30">
                          Coming Soon
                        </Badge>
                      )}
                    </div>
                    <p className="text-slate-400 text-sm mb-4">{integration.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {integration.features.map((feature, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-slate-800 rounded-full text-slate-400">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-teal-900/30 to-cyan-900/30 border-teal-500/30">
            <CardContent className="py-12">
              <h2 className="text-2xl font-bold text-white mb-4">Need a Custom Integration?</h2>
              <p className="text-slate-300 mb-6 max-w-xl mx-auto">
                We offer custom API integrations for enterprise clients. Connect your existing systems with WellNest Technologies.
              </p>
              <Button 
                className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:opacity-90"
                onClick={() => window.location.href = 'mailto:integrations@wellnesttechnologies.com'}
              >
                Contact Integration Team
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
